import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog.js';
import { Button } from '@/components/ui/button.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { getAdminSession, getAdminSessions, updateSessionTeacher } from '@/lib/api-calls.js';
import { AdminSession } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';

interface CancelSessionSubstituteByAdminProps {
  session: AdminSession;
  subRequest: AdminSession['substitutionRequests'][number];
  trigger: ReactNode;
}

export const CancelSessionSubstituteByAdmin = ({
  session,
  subRequest,
  trigger,
}: CancelSessionSubstituteByAdminProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: () =>
      updateSessionTeacher({
        sessionId: session.id,
        payload: { action: 'cancel_pending', sendConfirmationEmail: false },
      }),
  });

  const onSubmit = () => {
    return mutateAsync()
      .then(() => {
        toast({
          title: 'Sub-request canceled',
          description:
            'This substitution request has been canceled. The original teacher will be the teacher for this class.',
        });
        queryClient.invalidateQueries(getAdminSessions());
        queryClient.invalidateQueries(getAdminSession(subRequest.sessionId));
        setOpen(false);
      })
      .catch(() => {
        toast({
          title: 'Sub-request failed to cancel',
        });
      });
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel substitution request</AlertDialogTitle>
          <AlertDialogDescription>
            You are canceling an open substitution for this session.
            <p className="mt-2">
              Once canceled, the original coach: <b>{subRequest.requestedByCoach.fullNameOrEmail}</b> will still be the
              teacher for this class. Please double check the teacher's availability before canceling.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction>
            <Button
              loading={isPending}
              onClick={(e) => {
                e.preventDefault();
                onSubmit();
              }}
            >
              Cancel substitute request
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
